import React, { useEffect } from "react";
import {
  UnifiedContainer,
  BreadcrumbContainer,
  ChatContainer,
  WidgetContainer,
  ChatWrapper,
  WidgetFirstColumn,
  WidgetSecondColumn,
  WidgetChatWrapper,
  CustomGenericListStyle,
} from "../BaseUnifiedProfilePage.styled";
import BaseUnifiedProfilePage from "../BaseUnifiedProfilePage";
import Assistance from "../components/Assistance/Assistance";
import GeneralInfo from "../components/Cards/GeneralInfo";
import GarageCard from "../components/Cards/GarageCard";
import Appointments from "../components/Cards/Appointments";
import Notification from "../components/Cards/Notification";
import RepairOrders from "../components/Cards/RepairOrders";
import Notes from "../components/Cards/Notes";
import Opportunity from "../components/Cards/Opportunity";
import DealershipTeam from "../components/Cards/DealershipTeam";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import DynamicBreadcrumbs from "../../../../elements/v2/DynamicBreadcrumbs/DynamicBreadcrumbs";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../../hooks";
import Alerts from "../../../../elements/v2/Alerts/Alerts";
import { useUserCheckPermissions } from "../../../../hooks/useUserCheckPermissions";
import { PermissionCodes, PermissionLevels } from "../../../../utils/enums";

const TWO_SECOND_TIMEOUT = 2000;

const UnifiedProfilePage = observer(() => {
  const { unifiedCustomerProfileStore } = useStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromSearch = searchParams.get("from_search");
  const sourceUrl = unifiedCustomerProfileStore.typeUrl?.toLowerCase() || "";
  const { checkPermission } = useUserCheckPermissions();

  useEffect(() => {
    if (unifiedCustomerProfileStore.notAuthorized) {
      setTimeout(() => {
        navigate("/contacts");
      }, TWO_SECOND_TIMEOUT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unifiedCustomerProfileStore.notAuthorized]);

  const getDynamicHref = (typeUrl) => {
    const basePath = typeUrl.toLowerCase();
    if (
      ["vehicles", "assistants", "repair-orders", "appointments"].includes(
        basePath
      )
    ) {
      return `/v2/${basePath}`;
    }
    // Special case for contacts
    if (basePath === "contacts") {
      return "/contacts";
    }
    return `/${basePath}`;
  };

  const handleSourceClick = () => {
    // Navigate back to the source list
    const sourcePath = getDynamicHref(sourceUrl);
    if (fromSearch) {
      navigate(`${sourcePath}?search=${encodeURIComponent(fromSearch)}`);
    } else {
      navigate(sourcePath);
    }
  };

  const contactInfo = unifiedCustomerProfileStore.contactInfo[0] || {};

  // Format the source URL for display
  const getSourceLabel = (source) => {
    switch (source.toLowerCase()) {
      case "repair-orders":
        return "Repair Orders";
      case "appointments":
        return "Appointments";
      case "vehicles":
        return "Vehicles";
      case "inventory":
        return "Inventory";
      case "contacts":
        return "Contacts";
      default:
        return source.charAt(0).toUpperCase() + source.slice(1);
    }
  };

  const breadcrumbItems = [
    {
      label: getSourceLabel(sourceUrl || "contacts"), // Default to 'Contacts' if no source
      href: getDynamicHref(sourceUrl || "contacts"),
      onClick: handleSourceClick,
    },
    {
      label: contactInfo.contact_full_name || "Loading...",
    },
    {
      label: "UCP",
      current: true,
      style: { fontWeight: "bold" },
    },
  ];

  return (
    <BaseUnifiedProfilePage>
      {unifiedCustomerProfileStore.notAuthorized ? (
        <Alerts
          severity="warning"
          title="Access Denied"
          message="You don't have permission to view this contact."
        />
      ) : null}
      <BreadcrumbContainer>
        <DynamicBreadcrumbs
          items={breadcrumbItems}
          currentPath={location.pathname}
        />
      </BreadcrumbContainer>
      <UnifiedContainer>
        <WidgetChatWrapper>
          <WidgetContainer>
            <WidgetFirstColumn>
              {checkPermission(PermissionCodes.UCP_CONTACT_INFO, PermissionLevels.VIEW) && <GeneralInfo />}
              {checkPermission(PermissionCodes.UCP_TEAM, PermissionLevels.VIEW) && <DealershipTeam />}
              {checkPermission(PermissionCodes.UCP_APPOINTMENTS, PermissionLevels.VIEW) && <Appointments />}
              {(checkPermission(PermissionCodes.UCP_NOTIFICATIONS, PermissionLevels.VIEW) || checkPermission(PermissionCodes.UCP_NOTIFICATIONS, PermissionLevels.UPDATE)) && <Notification />}
              </WidgetFirstColumn>
            <WidgetSecondColumn>
              {checkPermission(PermissionCodes.UCP_GARAGE, PermissionLevels.VIEW) && <GarageCard />}
              {checkPermission(PermissionCodes.UCP_OPPORTUNITIES, PermissionLevels.VIEW) && <Opportunity />}
              {checkPermission(PermissionCodes.UCP_REPAIR_ORDERS, PermissionLevels.VIEW) && <RepairOrders />}
              {(checkPermission(PermissionCodes.UCP_NOTES, PermissionLevels.VIEW) &&
                checkPermission(PermissionCodes.UCP_NOTES, PermissionLevels.CREATE)) && <Notes />} 
            </WidgetSecondColumn>
          </WidgetContainer>
          <ChatContainer>
            <ChatWrapper>
              <Assistance />
            </ChatWrapper>
          </ChatContainer>
        </WidgetChatWrapper>
        <CustomGenericListStyle />
      </UnifiedContainer>
    </BaseUnifiedProfilePage>
  );
});

export default UnifiedProfilePage;
